/* Multizone BTU Guard — capacity-first, YMQ
   - YMQ fields:
       .capacity-select.zone-N  (wrapper div) → contains a <select> for zone N capacity
   - "How many zones?" pills/radios live in YMQ (we locate by label text)
   - Condenser option lives in YMQ (we locate by label text containing "Condenser")
   - Enforces: max total indoor BTU = (condenser BTU) + 4,000
   - Does NOT hide capacity sizes (24k/36k always remain available); only disables options that exceed remaining budget
   - Resets all zone capacity selects when condenser variant OR zone count changes
*/

// console.log("[Multizone] multizone.js loaded");

//////////////////// HELPERS ////////////////////



const isVisible = (el) => !!(el && el.offsetParent !== null);

function cleanText(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

// Find the VISIBLE capacity <select> for a zone: .capacity-select.zone-1 etc.
function getCapacitySel(zone) {
  const wrappers = document.querySelectorAll(`.capacity-select.zone-${zone}`);
  for (const wrap of wrappers) {
    const sel = wrap.tagName === "SELECT" ? wrap : wrap.querySelector("select");
    if (sel && isVisible(sel)) return sel;
  }
  return null;
}

// Prefer the inner YMQ spans when present
function textOf(el) {
  if (!el) return "";
  const inner = el.querySelector(".ymq-option-label") || el.querySelector(".label");
  if (inner) return inner.textContent.trim();
  return cleanText(el.value || el.textContent || "");
}

// Robust BTU parsing:
// - "18k", "18K BTU", "18 k" => 18000
// - "24,000 BTU" => 24000
// - "24000" => 24000
function parseBTU(str) {
  if (!str) return 0;
  const s = String(str)
    .toLowerCase()
    .replace(/,/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const kMatch = s.match(/(\d+(?:\.\d+)?)\s*k\b/);
  if (kMatch) return Math.round(parseFloat(kMatch[1]) * 1000);

  const nMatch = s.match(/(\d{2,6})/);
  if (!nMatch) return 0;

  return parseInt(nMatch[1], 10);
}

function roundUpToNextThousand(n) {
  return Math.ceil(n / 1000) * 1000;
}

function getZoneFromSelect(sel) {
  const wrap = sel.closest(".capacity-select");
  const m = wrap?.className?.match(/\bzone-(\d+)\b/);
  return m ? parseInt(m[1], 10) : null;
}

function muteRequired(sel) {
  if (!sel) return;
  if (sel.hasAttribute("required")) sel.dataset.mzWasRequired = "1";
  sel.removeAttribute("required");

  if (sel.getAttribute("aria-required") === "true") sel.dataset.mzWasAriaRequired = "1";
  sel.setAttribute("aria-required", "false");
}

function restoreRequired(sel) {
  if (!sel) return;
  if (sel.dataset.mzWasRequired === "1") sel.setAttribute("required", "required");
  if (sel.dataset.mzWasAriaRequired === "1") sel.setAttribute("aria-required", "true");
}

//////////////////// YMQ BLOCK DISCOVERY ////////////////////

function getYmqBlocks() {
  return Array.from(
    document.querySelectorAll(".ymq-option, .ymq-field, .ymq-row, .ymq-control, .ymq_option")
  );
}

function findYmqBlockByIncludesText(needle) {
  const n = String(needle || "").toLowerCase();
  const blocks = getYmqBlocks();
  return blocks.find((b) => (b.textContent || "").toLowerCase().includes(n)) || null;
}

//////////////////// CONDENSER / VARIANT PARSING ////////////////////

// Parse strings like:
// "6 Zone/55k BTU/230V Condenser" -> { zones:6, baseBTU:55000, voltage:230, maxBTU:59000 }
function parseVariantInfo(label) {
  if (!label) return null;
  const s = String(label).toLowerCase();

  const zonesMatch = s.match(/(\d+)\s*zones?\b/);
  const btuMatch = s.match(/(\d+\s*k?\s*)btu\b/);
  const voltMatch = s.match(/(\d{3})\s*v\b/);

  const zones = zonesMatch ? parseInt(zonesMatch[1], 10) : null;
  const baseBTU = btuMatch ? parseBTU(btuMatch[1]) : null;
  const voltage = voltMatch ? parseInt(voltMatch[1], 10) : null;

  if (!zones || !baseBTU) return null;

  // Business rule: max is 4k above condenser BTU. Round up to next 1k for clean budget steps.
  const maxBTU = roundUpToNextThousand(baseBTU + 4000);

  return { zones, baseBTU, voltage, maxBTU };
}

function getVariantTitle() {
  // Prefer the YMQ "Condenser" block (very common in these builders)
  const condenserBlock =
    findYmqBlockByIncludesText("condenser") ||
    findYmqBlockByIncludesText("outdoor unit") ||
    null;

  const readFromBlock = (block) => {
    if (!block) return "";

    // Native radios
    const checkedRadio = block.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      if (checkedRadio.value) return cleanText(checkedRadio.value);

      // Try associated label text
      const id = checkedRadio.id;
      if (id) {
        const lbl = block.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (lbl) return cleanText(lbl.textContent);
      }
    }

    // Pill/ARIA style radios
    const ariaChecked = block.querySelector('[role="radio"][aria-checked="true"]');
    if (ariaChecked) return cleanText(ariaChecked.textContent);

    // Pressed/active styles
    const active =
      block.querySelector(".active, .selected, .is-selected, [aria-pressed='true']");
    if (active) return cleanText(active.textContent);

    return "";
  };

  const fromCondenser = readFromBlock(condenserBlock);
  if (fromCondenser) return fromCondenser;

  // Global fallbacks (avoid hard-coded fallback strings)
  const globalChecked = Array.from(document.querySelectorAll('input[type="radio"]:checked'))
    .map((r) => cleanText(r.value || ""))
    .find((v) => /btu/i.test(v) && /condenser/i.test(v));
  if (globalChecked) return globalChecked;

  const ariaGlobal = Array.from(document.querySelectorAll('[role="radio"][aria-checked="true"]'))
    .map((el) => cleanText(el.textContent))
    .find((t) => /btu/i.test(t) && /condenser/i.test(t));
  if (ariaGlobal) return ariaGlobal;

  return "";
}

function getVariantInfo() {
  const title = getVariantTitle();
  if (!title) return null;
  return parseVariantInfo(title);
}

function maxForVariant() {
  const info = getVariantInfo();
  return info?.maxBTU || 24000;
}

function getVariantZoneCount() {
  const info = getVariantInfo();
  return info?.zones || 1;
}

function getVariantSignature() {
  const info = getVariantInfo();
  if (!info) return "unknown";
  return `${info.zones}|${info.baseBTU}|${info.voltage || ""}`;
}

function getZoneSelects(z) {
  const sels = [];
  document
    .querySelectorAll(
      `.capacity-select.zone-${z} select, 
       .line-set-select.zone-${z} select, 
       .handler-type.zone-${z} select`
    )
    .forEach((sel) => {
      if (sel && isVisible(sel)) sels.push(sel);
    });
  return sels;
}




//////////////////// ZONE COUNT (HOW MANY ZONES) ////////////////////

function getChosenZoneCount() {
  // Prefer locating the YMQ block by label text
  const zonesBlock =
    findYmqBlockByIncludesText("how many zones") ||
    findYmqBlockByIncludesText("how many zone") ||
    null;

  const readZonesFromBlock = (block) => {
    if (!block) return null;

    const checkedRadio = block.querySelector('input[type="radio"]:checked');
    if (checkedRadio?.value) {
      const n = parseInt(checkedRadio.value, 10);
      if (!Number.isNaN(n)) return n;
    }

    const ariaChecked = block.querySelector('[role="radio"][aria-checked="true"]');
    if (ariaChecked) {
      const n = parseInt(cleanText(ariaChecked.textContent), 10);
      if (!Number.isNaN(n)) return n;
    }

    // Sometimes the selected value is rendered as inline text next to the label
    // Try to find a number token in the block (as a last resort)
    const m = cleanText(block.textContent).match(/\b([2-9]|10)\b/);
    if (m) return parseInt(m[1], 10);

    return null;
  };

  const chosenRaw = readZonesFromBlock(zonesBlock);

  // Fallback to your original selector if present
  const fallbackChecked = document.querySelector(
    '.zone-number-question input[type="radio"]:checked'
  );
  const fallbackVal =
    fallbackChecked && fallbackChecked.value ? parseInt(fallbackChecked.value, 10) : null;

  const chosen = chosenRaw || fallbackVal || 1;

  // Never allow choosing more zones than the condenser supports
  const variantMaxZones = getVariantZoneCount();
  return Math.max(1, Math.min(chosen, variantMaxZones));
}

function eachActiveZone(fn) {
  const need = getChosenZoneCount();
  for (let i = 1; i <= need; i++) fn(i);
}

function getZoneBTU(z) {
  const capSel = getCapacitySel(z);
  if (!capSel) return 0;
  return parseBTU(textOf(capSel));
}

function collectActiveZoneBTUs() {
  const need = getChosenZoneCount();
  const arr = [];
  for (let i = 1; i <= need; i++) arr.push(getZoneBTU(i));
  return arr;
}

//////////////////// YMQ ERROR UI HELPERS ////////////////////

const getPlaceholderOption = (sel) =>
  Array.from(sel.options).find(
    (o) => !o.value || o.classList.contains("ymq-option-select-placeholder")
  );

function clearYmqErrors() {
  document.querySelectorAll(".ymq-option-error").forEach((el) => {
    el.textContent = "";
    el.style.display = "none";
  });

  document
document.querySelectorAll(".ymq-option-error").forEach((el) => {
    el.textContent = "";
    el.style.display = "none";
  });

  document
    .querySelectorAll(
      ".ymq-options-box, .ymq-option-box-error, .ymq-dropdown"
    )
    .forEach((el) => {
      el.classList.remove(
        "ymq-option-box-error",
        "ymq-error",
        "ymq-field-error",
        "ymq-option-error-border"
      );
      el.style.borderColor = "";
      el.style.boxShadow = "";
      el.style.outline = "";
    });

  document
    .querySelectorAll('.ymq-dropdown[aria-invalid="true"]')
    .forEach((el) => el.setAttribute("aria-invalid", "false"));
}

// Clear invalid styles that cause red borders (YMQ often uses classes/aria)
function clearYmqErrorStyles() {
  clearAllYmqErrors();

  document.querySelectorAll(".capacity-select select").forEach((sel) => {
    sel.removeAttribute("aria-invalid");
    sel.classList.remove("ymq-error", "ymq-option-error", "error", "is-invalid");
    const wrap = sel.closest(
      ".ymq-option, .ymq-field, .ymq-control, .ymq-row, .capacity-select"
    );
    if (wrap) wrap.classList.remove("ymq-error", "ymq-option-error", "error", "is-invalid");
  });
}

//////////////////// PAGE DETECTION ////////////////////

function isMultizonePage() {
  return !!document.querySelector(".capacity-select");
}

//////////////////// STATE ////////////////////

window.__mzInvalid = false;
let __mzLastVariantSignature = null;
let __mzLastChosenZoneCount = null;
let __mzHasInteracted = false;
let __mzIsResetting = false;
const __mzTouchedZone = {};
function markInteracted() {
  __mzHasInteracted = true;
}

function clearYmqErrorsIfNotTouched() {
  if (!__mzHasInteracted) clearYmqErrorStyles();
}

//////////////////// RESET ON VARIANT / ZONE COUNT CHANGES ////////////////////

function resetAllZoneFields() {
  __mzIsResetting = true;
  Object.keys(__mzTouchedZone).forEach(k => delete __mzTouchedZone[k]);

  const maxZones = getVariantZoneCount();

  for (let z = 1; z <= maxZones; z++) {
    const sel = getCapacitySel(z);
    if (!sel) continue;

    muteRequired(sel);

    const ph = getPlaceholderOption(sel);
    sel.value = ph ? ph.value : "";

  }

  // Clear YMQ red UI AFTER YMQ reacts
  setTimeout(() => {
    clearYmqErrors();
    clearYmqErrorStyles();
    __mzIsResetting = false;
  }, 0);

  // Some themes reapply errors late
  setTimeout(() => clearYmqErrorStyles(), 150);

  window.__mzInvalid = true;
}


function maybeHandleVariantChange() {
  const sig = getVariantSignature();
  if (sig && sig !== __mzLastVariantSignature) {
    __mzLastVariantSignature = sig;
    resetAllZoneFields();
clearYmqErrors();
  }
}

function maybeHandleZoneCountChange() {
  const chosen = getChosenZoneCount();

  if (__mzLastChosenZoneCount == null) {
    __mzLastChosenZoneCount = chosen;
    return;
  }

  if (chosen !== __mzLastChosenZoneCount) {
    __mzLastChosenZoneCount = chosen;
    resetAllZoneFields();
  }
}

//////////////////// CORE GUARD: BUDGET DISABLING + AUTO-CORRECT ////////////////////

function applyBudgetDisabling() {
  const max = maxForVariant();
  const btus = collectActiveZoneBTUs();

  eachActiveZone((z) => {
    const capSel = getCapacitySel(z);
    if (!capSel) return;

    const current = btus[z - 1] || 0;
    const usedOther = btus.reduce((sum, v, i) => (i === z - 1 ? sum : sum + v), 0);
    const remainingBudget = Math.max(0, max - usedOther);

    Array.from(capSel.options).forEach((o) => {
      const isPlaceholder =
        !o.value || o.classList.contains("ymq-option-select-placeholder");
      if (isPlaceholder) {
        o.disabled = false;
        o.hidden = false; // never hide sizes
        return;
      }

      const optBTU = parseBTU(o.textContent || o.value);

      // Never hide sizes (24k/36k etc should always be visible)
      o.hidden = false;

      // Keep current selection enabled even if it exceeds remainingBudget (we will correct separately)
      if (optBTU === current) {
        o.disabled = false;
        return;
      }

      o.disabled = optBTU > remainingBudget;
    });
  });
}

function enforceTotals() {
  const max = maxForVariant();

  let btus = collectActiveZoneBTUs();
  let used = btus.reduce((a, b) => a + b, 0);

  while (used > max) {
    let changedThisPass = false;

    // Start from last active zone
    for (let z = getChosenZoneCount(); z >= 1; z--) {
      const capSel = getCapacitySel(z);
      if (!capSel) continue;

      const currentBTU = btus[z - 1] || 0;
      const usedOther = used - currentBTU;
      const remainingBudget = Math.max(0, max - usedOther);

      // Find the largest option <= remainingBudget (only among enabled/visible options)
      const candidates = Array.from(capSel.options)
        .filter((o) => o.value && !o.classList.contains("ymq-option-select-placeholder"))
        .map((o) => ({ o, btu: parseBTU(o.textContent || o.value) }))
        .filter(({ btu }) => btu > 0 && btu <= remainingBudget)
        .sort((a, b) => b.btu - a.btu);

      if (candidates.length > 0) {
        const best = candidates[0];
        if (best.btu === currentBTU) continue;

        capSel.value = best.o.value;
        capSel.dispatchEvent(new Event("change", { bubbles: true }));

        btus = collectActiveZoneBTUs();
        used = btus.reduce((a, b) => a + b, 0);

        changedThisPass = true;
        break;
      } else {
        // No capacity fits for this zone -> reset to placeholder
        const ph = getPlaceholderOption(capSel);
        if (ph) {
          capSel.value = ph.value;
          capSel.dispatchEvent(new Event("change", { bubbles: true }));

          btus = collectActiveZoneBTUs();
          used = btus.reduce((a, b) => a + b, 0);

          changedThisPass = true;
          break;
        }
      }
    }

    if (!changedThisPass) break;
  }

  // invalid if still over max or any active zone is unselected
  const need = getChosenZoneCount();
  const anyMissing = btus.slice(0, need).some((b) => !b || b <= 0);
  window.__mzInvalid = used > max || anyMissing;

  // On first load (no interaction), wipe the red required state
  clearYmqErrorsIfNotTouched();
}

//////////////////// RECALC LOOP ////////////////////

function recalcAll() {
  if (!isMultizonePage()) {
    window.__mzInvalid = false;
    return;
  }

  maybeHandleVariantChange();
  maybeHandleZoneCountChange();

  applyBudgetDisabling();
  enforceTotals();
  applyBudgetDisabling();
}

//////////////////// EVENT LISTENERS ////////////////////

function hookCapacitySelects() {
  if (!isMultizonePage()) return;

  document.addEventListener("change", (e) => {
    const el = e.target;
    if (!el) return;

    // =========================
    // CAPACITY SELECT CHANGED
    // =========================
    if (el.matches(".capacity-select select") || el.closest(".capacity-select")) {
    if (__mzIsResetting) return;

    const sel = el.matches("select") ? el : el.querySelector("select");
    const zone = sel ? getZoneFromSelect(sel) : null;

    if (zone) {
        __mzTouchedZone[zone] = true;
        restoreRequired(sel);
    }

    markInteracted();
    setTimeout(recalcAll, 0);
    return;
    }


    // =========================
    // CONDENSER VARIANT CHANGED
    // =========================
    if (el.matches('input[type="radio"][name^="BTU/Zones"]')) {
      markInteracted();

      resetAllZoneFields();   
      clearYmqErrors();       // <-- clears red borders

      setTimeout(recalcAll, 0);
      return;
    }
  });
}


// Some YMQ setups dynamically insert fields after load; observe mutations.
function observeYmq() {
  if (!isMultizonePage()) return;

  const obs = new MutationObserver(() => {
    recalcAll();
  });

  obs.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false,
  });
}

//////////////////// INIT ////////////////////

function initMultizone() {
  if (!isMultizonePage()) return;

  try {
    hookCapacitySelects();
    observeYmq();

    recalcAll();
    clearYmqErrorsIfNotTouched();
  } catch (e) {
    console.error("[Multizone] init error:", e?.message || e, e?.stack || "");
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMultizone);
} else {
  initMultizone();
}

// Debug probe
window.mzProbe = function () {
  console.log("[Multizone] condenser title:", getVariantTitle());
  console.log("[Multizone] variant info:", getVariantInfo());
  console.log("[Multizone] max:", maxForVariant());
  console.log("[Multizone] chosen zones:", getChosenZoneCount());
  const need = getChosenZoneCount();
  for (let z = 1; z <= need; z++) {
    const sel = getCapacitySel(z);
    console.log(`[Z${z}]`, textOf(sel), "=>", getZoneBTU(z));
  }
  console.log("[Multizone] invalid:", window.__mzInvalid);
};
